import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridFilterModel,
  GridRowScrollEndParams,
} from '@mui/x-data-grid-pro';
import type { ReactNode } from 'react';
import type {
  BasicReview,
  FetchReviewsFiltersType,
} from '@shared/domain/review';
import type { FilterConfig } from '@shared/components/Filter.types';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import HandshakeIcon from '@mui/icons-material/Handshake';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button as MuiButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, GridLogicOperator } from '@mui/x-data-grid-pro';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import AuthorBadge from '@shared/components/AuthorBadge';
import CustomButton from '@shared/components/Buttons/Button';
import Filters from '@shared/components/Filters';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { PageSection } from '@shared/components/PageSection';
import { UserContext } from '@/shared/contexts/UserContext';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { theme } from '@shared/theme';
import { gradients } from '@shared/theme/patterns';
import CreateIntelligenceRequestModal from '../../../review-reader/components/CreateIntelligenceRequestModal';
import { useReviewContext } from '../../../review/context/ReviewContext';

interface CompanyProfileReviewsProps {
  reviews?: Array<BasicReview>;
  title: string;
  subtitle?: string;
  handleOpenReviewDialog: (reviewId?: number) => void;
  handleViewDealReviewClick?: (review: BasicReview) => void;
  companyId?: number;
  onReviewsLoaded?: (reviewIds: number[]) => void;
  onFiltersChanged?: (filters: FetchReviewsFiltersType) => void;
  emptyState?: ReactNode;
  /**
   * When true, skips the PageSection wrapper and renders only the content body.
   * The "Add Deal Review" button is still shown inline at the top-right.
   * Use this when embedded inside a container that already provides its own header.
   */
  bare?: boolean;
  /**
   * When true AND bare=true, suppresses the inline "Add Deal Review" button row.
   * Useful when the parent renders the button elsewhere (e.g. in an AccordionSummary).
   */
  hideButton?: boolean;
  /**
   * When true, renders a compact/lean layout suitable for half-viewport containers
   * (e.g. inside Account Plan). Columns and filter dropdowns are responsively
   * dropped as the container narrows. Row height and cell padding are also tightened.
   */
  lean?: boolean;
  /** When true, only fetches reviews authored by the current user */
  onlyOwnReviews?: boolean;
}

const RANGE_LABELS = [
  '0 - 25k',
  '25k - 50k',
  '50k - 100k',
  '100k - 250k',
  '250k - 500k',
  '500k - 1M',
  '1MM+',
];

const DEAL_TYPE_OPTIONS = ['New Business', 'Expansion/Upsell', 'Renewal'];

const DEAL_AREA_OPTIONS = [
  'IT',
  'Security',
  'Finance',
  'HR',
  'Legal',
  'Sales',
  'Marketing',
  'Customer Success',
  'Operations',
  'Engineering',
];

const OUTCOME_OPTIONS = ['Yes', 'No'];

const DEAL_DATE_OPTIONS = ['2021', '2022', '2023', '2024', '2025', '2026'];

const ThickDivider = styled.div`
  height: 8px;
  width: 100%;
  background: linear-gradient(to right, #f2f2f2, #fafafa);
  border-radius: 6px;
  margin: 24px 0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
`;

// Lean mode tracks container width to drive DataGridPro column
// visibility — the filter bar itself uses the shared `<Filters>`
// component, which wraps responsively via CSS grid.
const dealTypeBreakpoint = 860;
const dealCloseBreakpoint = 720;
const dealDateBreakpoint = 620;

const FILTER_FIELDS = ['dealArea', 'dealType', 'dealClose', 'dealSize', 'dealDate'] as const;

const emptyFilterState = (): Record<string, string | string[]> => ({
  dealArea: [],
  dealType: [],
  dealClose: [],
  dealSize: [],
  dealDate: [],
});

function CompanyProfileReviews({
  reviews = [],
  title,
  subtitle,
  handleOpenReviewDialog,
  handleViewDealReviewClick,
  companyId,
  onReviewsLoaded,
  onFiltersChanged,
  emptyState,
  bare = false,
  lean = false,
  hideButton = false,
  onlyOwnReviews = false,
}: CompanyProfileReviewsProps) {
  const { loading } = useReviewContext();
  const { user } = useContext(UserContext);
  const { openMessage } = useMessageService();
  const { isMobile } = useResponsive();
  const api = useApi();

  // Container width drives DataGrid columnVisibilityModel in lean mode.
  // A callback ref (leanRef) is the reliable pattern: it fires when the
  // element mounts/unmounts, so there are no timing or dual-ref issues.
  const [containerWidth, setContainerWidth] = useState<number>(9999);
  const activeObserverRef = useRef<ResizeObserver | null>(null);
  const leanRef = useCallback((node: HTMLDivElement | null) => {
    // Always disconnect the previous observer first
    if (activeObserverRef.current) {
      activeObserverRef.current.disconnect();
      activeObserverRef.current = null;
    }
    if (!lean || !node) {
      return;
    }
    // Synchronous initial measurement
    setContainerWidth(node.getBoundingClientRect().width || 9999);
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) {
        setContainerWidth(w);
      }
    });
    observer.observe(node);
    activeObserverRef.current = observer;
  }, [lean]);

  // Filter state for DataGridPro
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: GridLogicOperator.And,
  });

  // Current selections, keyed by FilterConfig id, driving the shared <Filters>.
  const [currentFilters, setCurrentFilters] = useState<Record<string, string | string[]>>(emptyFilterState);

  // Infinite scroll state
  const [allReviews, setAllReviews] = useState<BasicReview[]>(reviews);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [favoriteTogglingIds, setFavoriteTogglingIds] = useState<Set<number>>(
    new Set(),
  );
  const PAGE_SIZE = 100;

  // Use refs for callbacks to prevent dependency issues
  const onFiltersChangedRef = useRef(onFiltersChanged);
  const onReviewsLoadedRef = useRef(onReviewsLoaded);

  useEffect(() => {
    onFiltersChangedRef.current = onFiltersChanged;
    onReviewsLoadedRef.current = onReviewsLoaded;
  }, [onFiltersChanged, onReviewsLoaded]);

  // Translate DataGridPro filter model to backend API params
  const translateFilterModelToParams = (
    model: GridFilterModel,
  ): Record<string, any> => {
    const params: Record<string, any> = {};

    model.items.forEach((item) => {
      const { field, operator, value } = item;

      if (!value || (Array.isArray(value) && value.length === 0)) {
        return;
      }

      switch (field) {
        case 'dealType':
        case 'dealArea':
          // isAnyOf operator: multiple selection (OR within field)
          if (operator === 'isAnyOf' && Array.isArray(value)) {
            params[field] = value;
          }
          break;

        case 'dealClose':
          // is operator: single selection
          if (operator === 'is') {
            params[field] = value;
          }
          break;

        case 'dealDate':
          // isAnyOf operator: multiple year selection
          if (operator === 'isAnyOf' && Array.isArray(value)) {
            params.dealDate = value;
          }
          break;

        case 'dealSize':
          // between operator: range selection
          // Value format: [minValue, maxValue] as strings like '50k - 100k'
          if (operator === '>=' || operator === '<=') {
            if (operator === '>=') {
              const minIndex = RANGE_LABELS.indexOf(value);
              if (minIndex !== -1) {
                params.dealSizeMin = RANGE_LABELS[minIndex];
              }
            }
            if (operator === '<=') {
              const maxIndex = RANGE_LABELS.indexOf(value);
              if (maxIndex !== -1) {
                params.dealSizeMax = RANGE_LABELS[maxIndex];
              }
            }
          }
          break;

        default:
          break;
      }
    });

    return params;
  };

  // Load stub reviews (deal insights)
  const loadStubReviews = useCallback(async () => {
    if (!companyId) {
      return [];
    }

    try {
      const response = await api.get('/deal-insights', {
        params: { companyId },
      });

      if (response.error) {
        console.error('Error loading stub reviews:', response.message);
        return [];
      }

      return response.data || [];
    }
    catch (error) {
      console.error('Error loading stub reviews:', error);
      return [];
    }
  }, [companyId, api]);

  // Load reviews (initial or filtered)
  const loadReviews = useCallback(
    async (
      offset: number = 0,
      resetData: boolean = false,
      customFilterModel?: GridFilterModel,
    ) => {
      if (!companyId) {
        return;
      }

      if (resetData) {
        setAllReviews([]);
      }
      setIsLoadingMore(true);
      try {
        const modelToUse = customFilterModel || filterModel;
        const filterParams = translateFilterModelToParams(modelToUse);
        const params = {
          searchType: 'custom',
          companyId,
          limit: PAGE_SIZE,
          offset,
          excludeStatus: 'IN_PROGRESS', // Don't show in-progress reviews in company profile
          ...(onlyOwnReviews && user?.id ? { authorId: [user.id] } : {}),
          ...filterParams,
        };

        const response = await api.get('/review', { params });

        if (response.error) {
          console.error('Error loading reviews:', response.message);
          return;
        }

        const newReviews = response.data || [];

        // Load stub reviews only on initial load (offset === 0 and resetData === true)
        let stubReviews: BasicReview[] = [];
        if (resetData && offset === 0) {
          stubReviews = await loadStubReviews();
        }

        if (resetData) {
          const combinedReviews = [...newReviews, ...stubReviews];
          const sortedReviews = combinedReviews.sort((a, b) => {
            const aIsStub = a.reviewDepth === 'STUB';
            const bIsStub = b.reviewDepth === 'STUB';
            if (aIsStub === bIsStub)
              return 0;
            return aIsStub ? 1 : -1;
          });
          setAllReviews(sortedReviews);
          setCurrentOffset(0);
        }
        else {
          setAllReviews(prev => [...prev, ...newReviews]);
          setCurrentOffset(offset);
        }

        setHasNextPage(newReviews.length >= PAGE_SIZE);
      }
      catch (error) {
        console.error('Error loading reviews:', error);
      }
      finally {
        setIsLoadingMore(false);
      }
    },
    [companyId, filterModel, api, PAGE_SIZE, loadStubReviews, onlyOwnReviews, user?.id],
  );

  // Handle filter change - refetch from backend
  const handleFilterChange = useCallback(
    (newFilterModel: GridFilterModel) => {
      setFilterModel(newFilterModel);
      // Reset and refetch with new filters
      loadReviews(0, true, newFilterModel);

      // Convert GridFilterModel to FetchReviewsFiltersType for EnhancedCompanyData
      if (onFiltersChangedRef.current) {
        const filters: FetchReviewsFiltersType = {
          dealType: [],
          dealArea: [],
          dealWon: '',
          dealDate: '',
          dealSize: [0, RANGE_LABELS.length - 1],
        };

        newFilterModel.items.forEach((item) => {
          const { field, operator, value } = item;

          if (!value || (Array.isArray(value) && value.length === 0)) {
            return;
          }

          switch (field) {
            case 'dealType':
              if (operator === 'isAnyOf' && Array.isArray(value)) {
                filters.dealType = value;
              }
              break;

            case 'dealArea':
              if (operator === 'isAnyOf' && Array.isArray(value)) {
                filters.dealArea = value;
              }
              break;

            case 'dealClose':
              if (operator === 'is') {
                filters.dealWon = value;
              }
              break;

            case 'dealDate':
              if (operator === 'isAnyOf' && Array.isArray(value)) {
                filters.dealDate = value.join(',');
              }
              break;

            case 'dealSize':
              // Map string value back to index
              if (operator === '>=' || operator === '<=') {
                const currentSize = filters.dealSize || [
                  0,
                  RANGE_LABELS.length - 1,
                ];
                if (operator === '>=') {
                  const minIndex = RANGE_LABELS.indexOf(value);
                  if (minIndex !== -1) {
                    filters.dealSize = [minIndex, currentSize[1]];
                  }
                }
                if (operator === '<=') {
                  const maxIndex = RANGE_LABELS.indexOf(value);
                  if (maxIndex !== -1) {
                    filters.dealSize = [currentSize[0], maxIndex];
                  }
                }
              }
              break;

            default:
              break;
          }
        });

        onFiltersChangedRef.current(filters);
      }
    },
    [loadReviews],
  );

  // Build a DataGridPro filter model from the shared <Filters> state.
  const buildFilterModel = useCallback(
    (state: Record<string, string | string[]>): GridFilterModel => {
      const items: GridFilterItem[] = [];
      FILTER_FIELDS.forEach((field) => {
        const value = state[field];
        if (Array.isArray(value) && value.length > 0) {
          items.push({ field, operator: 'isAnyOf', value });
        }
      });
      return { items, logicOperator: GridLogicOperator.And };
    },
    [],
  );

  const handleSharedFilterChange = useCallback(
    (filterId: string, value: string | string[]) => {
      setCurrentFilters((prev) => {
        const updated = { ...prev, [filterId]: value };
        handleFilterChange(buildFilterModel(updated));
        return updated;
      });
    },
    [buildFilterModel, handleFilterChange],
  );

  const handleClearAllFilters = useCallback(() => {
    const cleared = emptyFilterState();
    setCurrentFilters(cleared);
    handleFilterChange(buildFilterModel(cleared));
  }, [buildFilterModel, handleFilterChange]);

  // Build the filter config array once per render — options are static.
  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      id: 'dealArea',
      label: 'Sold To',
      type: 'select',
      multiple: true,
      autocomplete: true,
      size: 'small',
      options: DEAL_AREA_OPTIONS.map(o => ({ value: o, name: o })),
      testId: 'filter-deal-area',
    },
    {
      id: 'dealType',
      label: 'Deal Type',
      type: 'select',
      multiple: true,
      autocomplete: true,
      size: 'small',
      options: DEAL_TYPE_OPTIONS.map(o => ({ value: o, name: o })),
      testId: 'filter-deal-type',
    },
    {
      id: 'dealClose',
      label: 'Outcome',
      type: 'select',
      multiple: true,
      autocomplete: true,
      size: 'small',
      options: OUTCOME_OPTIONS.map(o => ({
        value: o,
        name: o === 'Yes' ? 'Deal Won' : 'Deal Lost',
      })),
      testId: 'filter-outcome',
    },
    {
      id: 'dealSize',
      label: 'Deal Size',
      type: 'select',
      multiple: true,
      autocomplete: true,
      size: 'small',
      options: RANGE_LABELS.map(r => ({ value: r, name: r })),
      testId: 'filter-deal-size',
    },
    {
      id: 'dealDate',
      label: 'Deal Year',
      type: 'select',
      multiple: true,
      autocomplete: true,
      size: 'small',
      options: DEAL_DATE_OPTIONS.map(o => ({ value: o, name: o })),
      testId: 'filter-deal-year',
    },
  ], []);

  // Initial load when companyId changes
  useEffect(() => {
    // Only load reviews if we have both companyId and user (with auth token)
    if (companyId && user?.idToken) {
      loadReviews(0, true);
    }

    // Listen for favorite toggle events from other components
    const handleFavoriteToggle = (event: CustomEvent) => {
      const { reviewId, isFavorite } = event.detail;
      // Update the review in local state immediately
      setAllReviews(prevReviews =>
        prevReviews.map(review =>
          review.id === reviewId ? { ...review, isFavorite } : review,
        ),
      );
    };

    window.addEventListener(
      'favoriteToggled',
      handleFavoriteToggle as EventListener,
    );

    return () => {
      window.removeEventListener(
        'favoriteToggled',
        handleFavoriteToggle as EventListener,
      );
    };
  }, [companyId, user?.idToken]);

  useEffect(() => {
    if (onReviewsLoadedRef.current && allReviews.length > 0) {
      const reviewIds = allReviews
        .map(r => r.id)
        .filter((id): id is number => id !== undefined);
      onReviewsLoadedRef.current(reviewIds);
    }
  }, [allReviews]);

  const handleEngageAuthorClick = (
    reviewId?: number,
    targetUserId?: number,
  ) => {
    openMessage({
      modalType: 'action',
      header: 'Request Consultation',
      customBody: (
        <CreateIntelligenceRequestModal
          companyId={companyId}
          reviewId={reviewId}
          targetUserId={targetUserId}
        />
      ),
      size: 'large',
    });
  };

  // Handle favorite toggle
  const handleFavoriteToggle = async (
    reviewId: number,
    currentIsFavorite: boolean,
  ) => {
    if (!user || !user.idToken) {
      openMessage({
        header: 'Authentication Required',
        message: 'Please log in to favorite reviews.',
        type: 'error',
      });
      return;
    }

    setFavoriteTogglingIds(prev => new Set(prev).add(reviewId));

    try {
      const endpoint = currentIsFavorite
        ? `/review/${reviewId}/unfavorite`
        : `/review/${reviewId}/favorite`;
      const method = currentIsFavorite ? 'delete' : 'post';
      const response = await api[method](endpoint);

      if (response.error) {
        throw new Error(response.message || 'Failed to update favorite');
      }

      // Update the review in local state
      setAllReviews(prevReviews =>
        prevReviews.map(review =>
          review.id === reviewId
            ? { ...review, isFavorite: !currentIsFavorite }
            : review,
        ),
      );

      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new CustomEvent('favoriteToggled', {
          detail: { reviewId, isFavorite: !currentIsFavorite },
        }),
      );
    }
    catch (error) {
      console.error('Error toggling favorite:', error);
      openMessage({
        header: 'Error',
        message: 'Failed to update favorite. Please try again.',
        type: 'error',
      });
    }
    finally {
      setFavoriteTogglingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(reviewId);
        return newSet;
      });
    }
  };

  // Load more reviews for infinite scroll
  const loadMoreReviews = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || !companyId) {
      return;
    }

    const newOffset = currentOffset + PAGE_SIZE;
    await loadReviews(newOffset, false);
  }, [
    hasNextPage,
    isLoadingMore,
    companyId,
    currentOffset,
    PAGE_SIZE,
    loadReviews,
  ]);

  // Handle infinite scroll
  const handleRowScrollEnd = useCallback(
    async (_params: GridRowScrollEndParams) => {
      if (!hasNextPage || isLoadingMore || loading)
        return;

      const threshold = 5;
      if (allReviews.length - _params.viewportPageSize <= threshold) {
        await loadMoreReviews();
      }
    },
    [hasNextPage, isLoadingMore, loading, allReviews.length, loadMoreReviews],
  );

  // Define columns for the DataGridPro
  const columns: GridColDef[] = [
    {
      field: 'userName',
      headerName: 'Author',
      width: 120,
      sortable: true,
      filterable: false,
      renderCell: params => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <AuthorBadge
            userId={params.row.userId}
            user={params.row.user}
            avatarOnly={true}
            size="small"
            showTooltip={true}
          />
        </Box>
      ),
    },
    {
      field: 'dealArea',
      headerName: 'Sold To',
      width: 180,
      sortable: true,
      filterable: false,
      type: 'singleSelect',
      valueOptions: DEAL_AREA_OPTIONS,
    },
    {
      field: 'dealType',
      headerName: 'Deal Type',
      width: 200,
      sortable: true,
      filterable: false,
      type: 'singleSelect',
      valueOptions: DEAL_TYPE_OPTIONS,
    },
    {
      field: 'dealSize',
      headerName: 'Deal Size',
      width: 160,
      sortable: true,
      filterable: false,
      type: 'singleSelect',
      valueOptions: RANGE_LABELS,
    },
    {
      field: 'dealClose',
      headerName: 'Outcome',
      width: 160,
      sortable: true,
      filterable: false,
      type: 'singleSelect',
      valueOptions: OUTCOME_OPTIONS,
      renderCell: (params) => {
        const value = params.value as string;
        const isDealWon = value?.toLowerCase() === 'yes';
        const label = isDealWon
          ? 'Deal Won'
          : value?.toLowerCase() === 'no'
            ? 'Deal Lost'
            : value || '-';

        if (value?.toLowerCase() === 'yes' || value?.toLowerCase() === 'no') {
          return (
            <Chip
              label={label}
              size="small"
              sx={{
                backgroundColor: isDealWon
                  ? 'rgba(76, 175, 80, 0.15)'
                  : 'rgba(78, 78, 78, 0.15)',
                color: isDealWon ? '#2e7d32' : '#313131',
                border: `1px solid ${isDealWon ? '#4caf50' : '#3c3c3c'}`,
                fontWeight: 500,
              }}
            />
          );
        }
        return <span>{label}</span>;
      },
    },
    {
      field: 'dealDate',
      headerName: 'Deal Year',
      width: 130,
      sortable: true,
      filterable: false,
      type: 'singleSelect',
      valueOptions: DEAL_DATE_OPTIONS,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 1,
      display: 'flex',
      align: 'left',
      renderCell: (params) => {
        const isStubReview = params.row.fullReview.reviewDepth === 'STUB'
          && params.row.fullReview.status === 'STUB_COMPLETED';
        const isOwnReview = params.row.userId === user?.id;

        return (
          <Box
            sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}
          >
            <Tooltip
              title={
                params.row.fullReview.isFavorite
                  ? 'Remove from favorites'
                  : 'Add to favorites'
              }
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavoriteToggle(
                    params.row.fullReview.id,
                    params.row.fullReview.isFavorite || false,
                  );
                }}
                disabled={favoriteTogglingIds.has(params.row.fullReview.id)}
                size="small"
                sx={{
                  'color': params.row.fullReview.isFavorite
                    ? 'warning.main'
                    : 'action.active',
                  '&:hover': {
                    color: 'warning.main',
                  },
                }}
              >
                {params.row.fullReview.isFavorite
                  ? (
                      <StarIcon fontSize="small" />
                    )
                  : (
                      <StarBorderIcon fontSize="small" />
                    )}
              </IconButton>
            </Tooltip>
            {!isStubReview && (
              <Chip
                label="📖 View"
                size="small"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  if (handleViewDealReviewClick) {
                    handleViewDealReviewClick(params.row.fullReview);
                  }
                }}
                sx={{
                  'backgroundColor': 'transparent',
                  'color': '#0b74da',
                  'border': '1px solid #0b74da',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              />
            )}
            {isOwnReview
              ? (
                  <Chip
                    label={isStubReview ? 'Your Insight' : 'Your Review'}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.15)',
                      color: '#2e7d32',
                      border: '1px solid #4caf50',
                      fontWeight: 500,
                    }}
                  />
                )
              : (
                  <Chip
                    label={lean ? `📞 Connect` : '📞 Request Consultation'}
                    size="small"
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEngageAuthorClick(params.row.id, params.row.userId);
                    }}
                    sx={{
                      'backgroundColor': 'transparent',
                      'color': '#0b74da',
                      'border': '1px solid #0b74da',
                      '&:hover': {
                        opacity: 0.8,
                      },
                    }}
                  />
                )}
          </Box>
        );
      },
    },
  ];

  // Transform reviews into rows for DataGridPro
  const rows = allReviews.map((review) => {
    return {
      id: review.id || Math.random(),
      userId: review.userId || review.user?.id,
      userName: review.user?.username || review.user?.firstName || '-',
      user: review.user,
      dealArea: review.dealArea || '-',
      dealType: review.dealType || '-',
      dealSize: review.dealSize || '-',
      dealClose: review.dealClose || '-',
      dealDate: review.dealDate || '-',
      fullReview: review,
    };
  });

  // Lean column widths – static, always includes all columns.
  // The DataGrid columnVisibilityModel prop handles show/hide dynamically;
  // changing the columns array itself causes grid layout issues.
  const leanWidths: Record<string, number> = {
    userName: 80,
    dealArea: 140,
    dealType: 155,
    dealSize: 120,
    dealClose: 115,
    dealDate: 95,
  };
  const leanColumns = lean
    ? columns.map(col =>
        col.field in leanWidths ? { ...col, width: leanWidths[col.field] } : col,
      )
    : columns;

  // Drives DataGrid column visibility in lean mode. Thresholds mirror the CSS
  // @container breakpoints used on the filter items so both stay in sync.
  const columnVisibilityModel: GridColumnVisibilityModel = lean
    ? {
        dealType: containerWidth >= dealTypeBreakpoint,
        dealClose: containerWidth >= dealCloseBreakpoint,
        dealDate: containerWidth >= dealDateBreakpoint,
      }
    : {};

  const addReviewButton = (
    <CustomButton
      data-testid="add-deal-review-button"
      size="small"
      onClick={() => handleOpenReviewDialog()}
      startIcon={<HandshakeIcon />}
    >
      Add Deal Review
    </CustomButton>
  );

  const bodyContent = (
    <>
      {loading || (isLoadingMore && allReviews.length === 0)
        ? (
            <>
              <ThickDivider />
              <LoadingSpinner
                variant="pulse"
                size="large"
                message="Loading reviews..."
                className="loading-spinner--network-blue"
              />
            </>
          )
        : (
            <>
              {(rows.length > 0 || filterModel.items.length > 0) && (
                <Filters
                  filters={filterConfigs}
                  currentFilters={currentFilters}
                  onFilterChange={handleSharedFilterChange}
                  onClearAllFilters={handleClearAllFilters}
                  storagePrefix={`company-reviews-${companyId ?? 'all'}`}
                  showLabel={false}
                  disableAutoLoad
                />
              )}
              {rows.length > 0 ? (
                <div className="company-profile-reviews__grid-wrapper" ref={lean ? leanRef : undefined}>
                  {isMobile ? (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {allReviews.map((review) => {
                      const isDealWon = review.dealClose?.toLowerCase() === 'yes';
                      const outcomeLabel = isDealWon
                        ? 'Deal Won'
                        : review.dealClose?.toLowerCase() === 'no'
                          ? 'Deal Lost'
                          : review.dealClose || '-';
                      const isStubReview = review.reviewDepth === 'STUB'
                        && review.status === 'STUB_COMPLETED';

                      return (
                        <Card
                          key={review.id}
                          sx={{ boxShadow: 2, border: 1, borderColor: 'grey.300' }}
                        >
                          <CardContent>
                            <Stack spacing={1.5}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                  >
                                    Author:
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      height: '100%',
                                    }}
                                  >
                                    <AuthorBadge
                                      userId={review.userId}
                                      user={review.user}
                                      avatarOnly={true}
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                                {(review.dealClose?.toLowerCase() === 'yes'
                                  || review.dealClose?.toLowerCase() === 'no') && (
                                  <Chip
                                    label={outcomeLabel}
                                    size="small"
                                    sx={{
                                      backgroundColor: isDealWon
                                        ? '#4caf50'
                                        : '#f44336',
                                      color: 'white',
                                      fontWeight: 'bold',
                                    }}
                                  />
                                )}
                              </Box>

                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Chip
                                  label={`Sold To: ${review.dealArea || '-'}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`Type: ${review.dealType || '-'}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`Size: ${review.dealSize || '-'}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`Deal Year: ${review.dealDate || '-'}`}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={isStubReview ? 'Deal Insight' : 'Full Review'}
                                  size="small"
                                  sx={{
                                    backgroundColor: isStubReview
                                      ? 'rgba(33, 150, 243, 0.15)'
                                      : 'rgba(76, 175, 80, 0.15)',
                                    color: isStubReview ? '#1976d2' : '#2e7d32',
                                    border: `1px solid ${isStubReview ? '#2196f3' : '#4caf50'}`,
                                    fontWeight: 500,
                                  }}
                                />
                              </Box>

                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                {!isStubReview && handleViewDealReviewClick && (
                                  <MuiButton
                                    variant="contained"
                                    size="small"
                                    fullWidth
                                    onClick={() => handleViewDealReviewClick(review)}
                                    sx={{
                                      'textTransform': 'none',
                                      'backgroundColor': '#0b74da',
                                      '&:hover': {
                                        backgroundColor: '#095fae',
                                      },
                                    }}
                                  >
                                    📖 Read Deal Review
                                  </MuiButton>
                                )}
                                {review.userId === user?.id
                                  ? (
                                      <Chip
                                        label={isStubReview ? 'Your Insight' : 'Your Review'}
                                        size="small"
                                        sx={{
                                          backgroundColor: 'rgba(76, 175, 80, 0.15)',
                                          color: '#2e7d32',
                                          border: '1px solid #4caf50',
                                          fontWeight: 500,
                                        }}
                                      />
                                    )
                                  : (
                                      <MuiButton
                                        variant="outlined"
                                        size="small"
                                        onClick={() =>
                                          handleEngageAuthorClick(
                                            review.id,
                                            review.userId,
                                          )}
                                        sx={{
                                          textTransform: 'none',
                                          minWidth: 'auto',
                                          px: 2,
                                        }}
                                      >
                                        📞 Request Call
                                      </MuiButton>
                                    )}
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                    {isLoadingMore && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <LoadingSpinner variant="pulse" size="medium" className="loading-spinner--network-blue" />
                      </Box>
                    )}
                  </Stack>
                )
                  : (
                      <DataGridPro
                        rows={rows}
                        columns={leanColumns}
                        columnVisibilityModel={columnVisibilityModel}
                        pagination={false}
                        filterMode="server"
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterChange}
                        onRowsScrollEnd={handleRowScrollEnd}
                        scrollEndThreshold={300}
                        rowHeight={lean ? 42 : 52}
                        initialState={{
                          sorting: {
                            sortModel: [{ field: 'userId', sort: 'asc' }],
                          },
                        }}
                        disableRowSelectionOnClick
                        loading={isLoadingMore}
                        autoHeight
                        disableMultipleColumnsFiltering={false}
                        slotProps={{
                          filterPanel: {
                            logicOperators: [GridLogicOperator.And],
                          },
                        }}
                        sx={{
                          'boxShadow': 2,
                          'border': 2,
                          'borderColor': theme.palette.primary.light,
                          'height': '100%',
                          'width': '100%',
                          '& .MuiDataGrid-toolbarContainer': {
                            padding: '8px',
                            borderBottom: `1px solid ${theme.palette.grey[300]}`,
                            backgroundColor: '#fff',
                          },
                          '& .MuiDataGrid-columnHeaders': {
                            'background': gradients.reviewBanner,
                            'color': '#1976d2',
                            'fontWeight': 'bold',
                            'fontSize': '0.875rem',
                            'borderBottom': '2px solid #1976d2',
                            'position': 'sticky',
                            'top': 0,
                            'zIndex': 1,
                            '& .MuiDataGrid-columnHeaderTitle': {
                              fontWeight: 'bold',
                            },
                          },
                          '& .MuiDataGrid-columnHeader': {
                            backgroundColor: 'transparent',
                          },
                          '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                          },
                          '& .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                          },
                          '& .MuiDataGrid-row': {
                            borderTop: `1px solid ${theme.palette.grey[200]}`,
                            borderBottom: 0,
                          },
                          '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                          },
                          '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                          },
                          ...(lean && {
                            '& .MuiDataGrid-cell': { px: 1 },
                            '& .MuiDataGrid-columnHeader': { px: 1 },
                          }),
                          '& .MuiDataGrid-filler': {
                            backgroundColor: theme.palette.grey[50],
                          },
                          '& .MuiDataGrid-footerContainer': {
                            display: 'none', // Hide footer since we use infinite scroll
                          },
                        }}
                      />
                    )}
                </div>
              ) : emptyState && filterModel.items.length === 0 ? (
                emptyState
              ) : filterModel.items.length > 0 ? (
                  // Filters active but nothing matches
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 6,
                      px: 3,
                      textAlign: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.5,
                      }}
                    >
                      <FilterAltOffIcon sx={{ fontSize: 32, color: '#1976d2' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      No reviews match your filters
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360 }}>
                      Try broadening your search by adjusting or clearing the active filters.
                    </Typography>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      startIcon={<FilterAltOffIcon />}
                      onClick={handleClearAllFilters}
                      sx={{ mt: 1 }}
                    >
                      Clear Filters
                    </MuiButton>
                  </Box>
                )
              : (
                  // No reviews at all
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 7,
                      px: 3,
                      textAlign: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e3f0fb 0%, #cfe3f7 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 0.5,
                        boxShadow: '0 4px 14px rgba(11, 116, 218, 0.18)',
                      }}
                    >
                      <HandshakeIcon sx={{ fontSize: 36, color: '#0b74da' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      No deal reviews yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, lineHeight: 1.6 }}>
                      Be the first to share your experience selling to this company.
                      Your insights help the entire community close more deals.
                    </Typography>
                    <MuiButton
                      variant="contained"
                      size="medium"
                      startIcon={<HandshakeIcon />}
                      onClick={() => handleOpenReviewDialog()}
                      sx={{
                        mt: 1,
                        px: 3,
                        fontWeight: 600,
                        background: 'linear-gradient(90deg, #095fae 0%, #0b74da 100%)',
                        boxShadow: '0 3px 10px rgba(52,152,219,0.35)',
                      }}
                    >
                      Add the First Review
                    </MuiButton>
                  </Box>
                )}
            </>
          )}
    </>
  );

  if (bare) {
    return (
      <Box>
        {!hideButton && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, px: 2, pt: 1 }}>
            {addReviewButton}
          </Box>
        )}
        {bodyContent}
      </Box>
    );
  }

  return (
    <PageSection
      title={title}
      subtitle={subtitle}
      collapsible={true}
      defaultExpanded={true}
      adornment={addReviewButton}
    >
      {bodyContent}
    </PageSection>
  );
}

export default CompanyProfileReviews;
